import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { ChipGroup } from '@/components/app/chip-group'
import { EmptyState } from '@/components/app/empty-state'
import { ErrorBanner } from '@/components/app/error-banner'
import { Fab } from '@/components/app/fab'
import { Field, fieldClass } from '@/components/app/field'
import { ListSkeleton } from '@/components/app/list-skeleton'
import { Screen } from '@/components/app/screen'
import { ScreenHeader } from '@/components/app/screen-header'
import { SubmitButton } from '@/components/app/submit-button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { apiPost } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/api-error'
import { getAuthUser } from '@/lib/auth'
import { CATEGORY_LABELS, EXPENSE_CATEGORIES, INCOME_CATEGORIES, MONTHS_FR } from '@/lib/constants'
import { formatDateShort, formatFCFA } from '@/lib/format'
import { currentMonth, currentYear } from '@/lib/period'
import { bilanQuery, transactionsQuery } from '@/lib/queries/finance'
import { canAddTransaction, canViewFinance } from '@/lib/roles'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_staff/finances/')({
  beforeLoad: () => {
    const user = getAuthUser()
    if (!user || !canViewFinance(user.roles)) throw redirect({ to: '/accueil' })
  },
  component: FinanceScreen,
})

function FinanceScreen() {
  const { roles } = Route.useRouteContext()
  const year = currentYear()
  const [month, setMonth] = useState(currentMonth())
  const [sheetOpen, setSheetOpen] = useState(false)

  const bilan = useQuery(bilanQuery(year, month))
  const transactions = useQuery(transactionsQuery(year, month))

  const canAdd = canAddTransaction(roles)
  const netBalance = bilan.data?.netBalance ?? 0

  return (
    <Screen withTabBar>
      <ScreenHeader title="Finances" subtitle={String(year)} />

      <div className="flex flex-col gap-5 px-5">
        <ChipGroup
          options={MONTHS_FR.map((label, i) => ({ value: String(i + 1), label }))}
          value={String(month)}
          onChange={(value) => setMonth(Number(value))}
        />

        <div className="grid grid-cols-2 gap-3">
          <AmountCard
            icon={<ArrowUpCircle size={18} color="#4CAF50" aria-hidden="true" />}
            label="Recettes"
            amount={bilan.data?.totalIncome}
            tone="text-success"
          />
          <AmountCard
            icon={<ArrowDownCircle size={18} color="#FF6B6B" aria-hidden="true" />}
            label="Dépenses"
            amount={bilan.data?.totalExpense}
            tone="text-danger"
          />
        </div>

        <div className="rounded-[14px] border border-navy-800 bg-navy-900 px-4 py-4">
          <p className="text-[13px] text-steel">Solde net du mois</p>
          <p
            className={cn(
              'mt-1 text-[26px] font-bold tabular-nums',
              netBalance >= 0 ? 'text-gold' : 'text-danger',
            )}
          >
            {netBalance >= 0 ? '+' : ''}
            {formatFCFA(netBalance)}
          </p>
        </div>

        <section>
          <h2 className="mb-2.5 text-[15px] font-semibold text-chalk">Dernières opérations</h2>

          {transactions.isPending && <ListSkeleton rows={4} />}
          {transactions.data?.data.length === 0 && (
            <EmptyState message="Aucune opération pour ce mois." />
          )}

          <ul className="flex flex-col gap-2">
            {transactions.data?.data.map((tx) => {
              const isIncome = tx.type === 'INCOME'
              return (
                <li
                  key={tx.id}
                  className="flex items-center gap-3 rounded-[14px] border border-navy-800 bg-navy-900 px-4 py-3"
                >
                  {isIncome ? (
                    <ArrowUpCircle size={18} color="#4CAF50" aria-label="Recette" />
                  ) : (
                    <ArrowDownCircle size={18} color="#FF6B6B" aria-label="Dépense" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-semibold text-chalk">{tx.label}</p>
                    <p className="text-[12px] text-steel">
                      {CATEGORY_LABELS[tx.category] ?? tx.category} · {formatDateShort(tx.date)}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 text-[15px] font-semibold tabular-nums',
                      isIncome ? 'text-success' : 'text-danger',
                    )}
                  >
                    {isIncome ? '+' : '−'}
                    {formatFCFA(tx.amount)}
                  </span>
                </li>
              )
            })}
          </ul>
        </section>
      </div>

      {canAdd && <Fab onClick={() => setSheetOpen(true)} label="Nouvelle opération" />}

      <TransactionSheet open={sheetOpen} onOpenChange={setSheetOpen} />
    </Screen>
  )
}

function AmountCard({
  icon,
  label,
  amount,
  tone,
}: {
  icon: React.ReactNode
  label: string
  amount?: number
  tone: string
}) {
  return (
    <div className="rounded-[14px] border border-navy-800 bg-navy-900 px-4 py-3.5">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-[12px] text-steel">{label}</span>
      </div>
      <p className={cn('mt-1 text-[17px] font-bold tabular-nums', tone)}>
        {amount === undefined ? '—' : formatFCFA(amount)}
      </p>
    </div>
  )
}

interface TransactionSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function TransactionSheet({ open, onOpenChange }: TransactionSheetProps) {
  const queryClient = useQueryClient()

  const [type, setType] = useState<'INCOME' | 'EXPENSE'>('INCOME')
  const [category, setCategory] = useState('TITHE')
  const [amount, setAmount] = useState('')
  const [label, setLabel] = useState('')
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [error, setError] = useState<string | null>(null)

  const categories = type === 'INCOME' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES

  function switchType(next: 'INCOME' | 'EXPENSE') {
    setType(next)
    // La catégorie doit rester cohérente avec le type : le back-end rejette
    // une catégorie de dépense sur une recette (refine du DTO).
    setCategory(next === 'INCOME' ? 'TITHE' : 'RENT')
  }

  const mutation = useMutation({
    mutationFn: () =>
      apiPost('finance/transactions', {
        type,
        category,
        amount: Number(amount),
        label: label.trim(),
        date,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance'] })
      onOpenChange(false)
      setAmount('')
      setLabel('')
      setError(null)
    },
    onError: async (err) => {
      setError(getApiErrorMessage(err, "Impossible d'enregistrer l'opération."))
    },
  })

  // Les montants sont des entiers FCFA strictement positifs (z.int().min(1)).
  const parsedAmount = Number(amount)
  const canSubmit =
    label.trim().length > 0 &&
    Number.isInteger(parsedAmount) &&
    parsedAmount > 0 &&
    date.length === 10

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (canSubmit && !mutation.isPending) mutation.mutate()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[90dvh] border-navy-800 bg-navy-900">
        <SheetHeader>
          <SheetTitle className="text-chalk">Nouvelle opération</SheetTitle>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 overflow-y-auto px-5 pb-6">
          <div className="flex gap-2">
            {(['INCOME', 'EXPENSE'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => switchType(option)}
                aria-pressed={type === option}
                className={cn(
                  'flex-1 rounded-[14px] border py-3 text-[15px] font-semibold transition-colors',
                  type === option
                    ? option === 'INCOME'
                      ? 'border-success bg-success-bg text-success'
                      : 'border-danger-border bg-danger-bg text-danger'
                    : 'border-navy-800 text-steel',
                )}
              >
                {option === 'INCOME' ? 'Recette' : 'Dépense'}
              </button>
            ))}
          </div>

          <ChipGroup
            label="Catégorie"
            options={categories.map((c) => ({ value: c.value, label: c.label }))}
            value={category}
            onChange={setCategory}
          />

          <Field label="Montant (FCFA)">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))}
              placeholder="25000"
              className={fieldClass}
            />
          </Field>

          <Field label="Libellé">
            <input
              type="text"
              maxLength={200}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Quête du dimanche"
              className={fieldClass}
            />
          </Field>

          <Field label="Date">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={fieldClass}
            />
          </Field>

          <ErrorBanner message={error} />

          <SubmitButton disabled={!canSubmit} loading={mutation.isPending}>
            Enregistrer
          </SubmitButton>
        </form>
      </SheetContent>
    </Sheet>
  )
}
